import { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, Shield, Users, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

export default function RolesPage() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/api/roles');
      const data = response.data.data || response.data || [];
      setRoles(Array.isArray(data) ? data : (data.items || []));
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const filteredRoles = roles.filter(r => (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (r.description || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const getRoleIcon = (name: string) => {
    const n = (name || '').toLowerCase();
    if (n.includes('admin')) return { icon: Shield, color: 'bg-red-100 text-red-600' };
    if (n.includes('delegate')) return { icon: Users, color: 'bg-blue-100 text-blue-600' };
    if (n.includes('merchant')) return { icon: Users, color: 'bg-green-100 text-green-600' };
    return { icon: Shield, color: 'bg-gray-100 text-gray-600' };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              الصلاحيات
            </h1>
            <p className="text-gray-600 mt-2">إدارة الأدوار والصلاحيات في النظام</p>
          </div>
          <Button onClick={() => navigate('/roles/new')} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" />إنشاء دور جديد
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center"><Shield className="w-6 h-6 text-indigo-600" /></div><div><p className="text-sm text-gray-600">الأدوار</p><p className="text-2xl font-bold">{roles.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-600">عدد المستخدمين</p><p className="text-2xl font-bold text-green-700">{roles.filter(r => r.isActive !== false).length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-purple-600" /></div><div><p className="text-sm text-gray-600">الصلاحيات</p><p className="text-2xl font-bold text-purple-700">-</p></div></CardContent></Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="بحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border rounded-lg" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-indigo-50 border-b border-indigo-200">
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <Shield className="w-5 h-5" />قائمة الأدوار ({filteredRoles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (<div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div><p>جاري التحميل...</p></div>) :
            filteredRoles.length === 0 ? (<div className="text-center py-12"><Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">لا توجد أدوار</p></div>) :
            (<div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-right p-3 font-semibold text-sm">#</th>
                <th className="text-right p-3 font-semibold text-sm">اسم الدور</th>
                <th className="text-right p-3 font-semibold text-sm">الوصف</th>
                <th className="text-right p-3 font-semibold text-sm">المستخدمين</th>
                <th className="text-right p-3 font-semibold text-sm">الإجراءات</th>
              </tr></thead>
              <tbody>
                {filteredRoles.map((role, idx) => {
                  const { icon: Icon, color } = getRoleIcon(role.name);
                  return (
                    <tr key={role.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-600">{idx + 1}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold">{role.name}</p>
                            <p className="text-xs text-gray-500">{role.nameAr || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-sm max-w-xs truncate">{role.description || '-'}</td>
                      <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${role.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{role.isActive !== false ? 'مستخدم' : 'E97D'}</span></td>
                      <td className="p-3"><div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/roles/${role.id}`)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm"><Trash2 className="w-4 h-4" /></Button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
